import React, { useRef } from 'react';
import { motion, useScroll, useSpring, useTransform } from 'framer-motion';
import { heroImage, showcase } from '../../data/site';
import { useLanguage } from '../../contexts/LanguageContext';
import { useMotionPreferences } from '../../hooks/useMotionPreferences';
import { usePointerParallax } from '../../hooks/usePointerParallax';
import { RacketObject } from './RacketObject';
import { ShowcaseWord } from './ShowcaseWord';

/**
 * Scroll-driven cinematic scene sitting between the hero and the feature grid.
 *
 * The depth is faked with five independently-mapped layers:
 *   background court → racket object → neon lighting → editorial type → foreground grid
 * Each layer reads the same scroll progress but converts it into different
 * transform distances, which is what reads as parallax depth.
 *
 * Only transform and opacity are animated. On touch/small screens the travel and
 * scale deltas are damped; with prefers-reduced-motion the whole thing collapses
 * into a static, readable composition.
 */
export function CinematicShowcase() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    reduced,
    isMobile,
    parallax,
    tiltEnabled
  } = useMotionPreferences();
  const {
    t
  } = useLanguage();

  // Travel multiplier (0 when reduced, 0.4 on mobile) and a gentler scale delta on phones.
  const p = parallax;
  const scaleAmp = reduced ? 0 : isMobile ? 0.45 : 1;
  const sc = (value: number) => 1 + (value - 1) * scaleAmp;
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end end']
  });
  const progress = useSpring(scrollYProgress, {
    stiffness: 90,
    damping: 26,
    restDelta: 0.001
  });
  const pointer = usePointerParallax(tiltEnabled && !isMobile);
  const pointerX = useTransform(pointer.x, v => v * 10);
  const pointerY = useTransform(pointer.y, v => v * 8);
  const pointerBgX = useTransform(pointer.x, v => v * -14);

  /* ---------- Layer 1 — background court (slowest) ---------- */
  const bgY = useTransform(progress, [0, 1], [-40 * p, 70 * p]);
  const bgScale = useTransform(progress, [0, 1], [sc(1.12), sc(1.3)]);
  const bgOpacity = useTransform(progress, [0, 0.45, 0.8, 1], [0.32, 0.22, 0.16, 0.1]);

  /* ---------- Layer 2 — the object ---------- */
  const racketY = useTransform(progress, [0, 0.25, 0.55, 0.8, 1], [130 * p, 74 * p, 0, -12 * p, -150 * p]);
  const racketX = useTransform(progress, [0, 0.25, 0.55, 1], [-70 * p, -34 * p, 0, 0]);
  const racketScale = useTransform(progress, [0, 0.25, 0.55, 0.8, 1], [sc(0.74), sc(0.86), sc(1.0), sc(1.06), sc(0.9)]);
  const rotateY = useTransform(progress, [0, 0.25, 0.55, 0.8, 1], [10 * p, 7 * p, 2 * p, -2 * p, -6 * p]);
  const rotateX = useTransform(progress, [0, 0.25, 0.55, 0.8, 1], [7 * p, 4 * p, 1 * p, -1 * p, -4 * p]);
  const rotateZ = useTransform(progress, [0, 0.55, 1], [-7 * p, 0, 4 * p]);
  const racketOpacity = useTransform(progress, [0, 0.06, 0.88, 1], [0, 1, 1, 0.05]);

  /* ---------- Layer 3 — lighting ---------- */
  const haloOpacity = useTransform(progress, [0, 0.35, 0.62, 0.85, 1], [0.2, 0.45, 0.95, 0.8, 0.15]);
  const sweepX = useTransform(progress, [0.55, 0.8], ['-70%', '190%']);
  const sweepOpacity = useTransform(progress, [0.54, 0.6, 0.74, 0.8], [0, 0.9, 0.9, 0]);
  const auraScale = useTransform(progress, [0, 0.6, 1], [sc(0.9), sc(1.15), sc(0.95)]);

  /* ---------- Layer 4 — editorial type ---------- */
  const introY = useTransform(progress, [0, 0.25, 0.55], [50 * p, 0, 110 * p]);
  const introOpacity = useTransform(progress, [0, 0.08, 0.3, 0.5], [0, 1, 1, 0]);

  /* ---------- Layer 5 — foreground + exit blend ---------- */
  const gridY = useTransform(progress, [0, 1], [0, 200 * p]);
  const exitOpacity = useTransform(progress, [0.72, 1], [0, 1]);
  const staticScene = reduced;
  return <section ref={sectionRef} aria-label="Pulse racket showcase" onPointerMove={pointer.onPointerMove} onPointerLeave={pointer.onPointerLeave} className={`relative mt-24 w-full bg-navy-deep ${staticScene ? 'py-24' : 'h-[240vh] md:h-[380vh]'}`} data-id="element-179" data-mp="3ti8hgpyunv6">
      <div className={`${staticScene ? 'relative' : 'sticky top-0 h-screen'} flex w-full items-center justify-center overflow-hidden`} data-id="element-180" data-mp="jbj8yhyen3dc">
        {/* Layer 1 — court background */}
        <motion.div aria-hidden="true" style={staticScene ? {
        opacity: 0.2
      } : {
        y: bgY,
        x: pointerBgX,
        scale: bgScale,
        opacity: bgOpacity,
        willChange: 'transform, opacity'
      }} className="absolute inset-0" data-id="element-181" data-mp="acecriabfm4c">
          <img src={heroImage} alt="" className="h-full w-full object-cover" data-id="element-182" data-mp="xzcqi871wfyk" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#07051A] via-[#0E0B2C]/85 to-[#07051A]" data-id="element-183" data-mp="t0t303ct6w43" />

        {/* Layer 3a — ambient neon aura behind everything */}
        <motion.div aria-hidden="true" style={staticScene ? undefined : {
        scale: auraScale,
        opacity: haloOpacity,
        willChange: 'transform, opacity'
      }} className="pointer-events-none absolute inset-0" data-id="element-184" data-mp="3drafdwqo6ppp">
          <div className="absolute left-1/2 top-1/2 h-[70vmin] w-[70vmin] -translate-x-1/2 -translate-y-1/2 rounded-full bg-grape/25 blur-[120px]" data-id="element-185" data-mp="22aplp4fhxoj5" />
          <div className="absolute -left-20 top-1/3 h-[42vmin] w-[42vmin] rounded-full bg-electric/25 blur-[110px]" data-id="element-186" data-mp="272qtoe5mst3d" />
          <div className="absolute -right-16 bottom-1/4 h-[46vmin] w-[46vmin] rounded-full bg-magenta/25 blur-[110px]" data-id="element-187" data-mp="2c2d6imhjs7bn" />
        </motion.div>

        {/*
          Layer 2 — the object, on its own perspective stage.
          The stage (not the image) carries the screen blend: it is a direct child
          of the sticky container, so its backdrop is the court + neon layers and
          the asset's black backdrop is knocked out entirely. It also sits below
          the type layer so the racket can never cover the heading.
         */}
        <div className={`absolute inset-0 z-10 flex items-center justify-center [perspective:1400px] ${showcase.racketNeedsBlend ? 'mix-blend-screen' : ''}`} data-id="element-188" data-mp="1ywerq8iwsz69">
          <motion.div style={staticScene ? undefined : {
          y: racketY,
          x: racketX,
          scale: racketScale,
          rotateX,
          rotateY,
          rotateZ,
          opacity: racketOpacity,
          transformStyle: 'preserve-3d',
          willChange: 'transform, opacity'
        }} className="relative mt-[7vh] h-[56vmin] w-[56vmin] max-h-[560px] max-w-[560px] md:h-[54vmin] md:w-[54vmin]" data-id="element-189" data-mp="27xuze8ican6s">
            <motion.div style={staticScene ? undefined : {
            x: pointerX,
            y: pointerY
          }} className="h-full w-full" data-id="element-190" data-mp="30n1yb8lphesk">
              <RacketObject sweepX={sweepX} sweepOpacity={sweepOpacity} haloOpacity={haloOpacity} reduced={staticScene} data-id="element-191" data-mp="3f98q9hnuhrbq" />
            </motion.div>
          </motion.div>
        </div>

        {/* Layer 4 — editorial type, always painted above the object */}
        <div className="pointer-events-none absolute inset-0 z-20" data-id="element-192" data-mp="3a8up8jknvhn7">
          {/* Layer 4a — intro copy, drifting at its own rate */}
          <motion.div style={staticScene ? undefined : {
          y: introY,
          opacity: introOpacity,
          willChange: 'transform, opacity'
        }} className="pointer-events-none absolute inset-x-0 top-[9%] mx-auto max-w-xl px-6 text-center" data-id="element-193" data-mp="2wgem7irrfjma">
            <p className="text-[0.65rem] font-semibold uppercase tracking-[0.32em] text-magenta sm:text-xs" data-id="element-194" data-mp="2djn4vevs3ekv">
              {t.showcase.eyebrow}
            </p>
            <h2 className="neon-text mt-4 font-display text-2xl font-black uppercase leading-[1.05] tracking-tight text-white sm:text-4xl" data-id="element-195" data-mp="2ii6ubp9kz8hz">
              {t.showcase.title}
            </h2>
            <p className="mx-auto mt-4 max-w-md text-xs leading-relaxed text-white/55 sm:text-sm" data-id="element-196" data-mp="2t2y4z9qpxabs">
              {t.showcase.caption}
            </p>
          </motion.div>

          {/* Layer 4b — editorial words around the object (desktop) */}
          <div className="pointer-events-none absolute inset-0 hidden items-center justify-between px-[7vw] md:flex" data-id="element-197" data-mp="2f1vhcsk2rryr">
            <ShowcaseWord word={t.showcase.words[0]} index={0} progress={progress} reduced={staticScene} className="-rotate-90 origin-center" data-id="element-198" data-mp="2aan93zuhecn6" />
            <ShowcaseWord word={t.showcase.words[2]} index={2} progress={progress} reduced={staticScene} className="rotate-90 origin-center" data-id="element-199" data-mp="20e0s5m5a05qf" />
          </div>
          <div className="pointer-events-none absolute inset-x-0 bottom-[12%] hidden justify-center md:flex" data-id="element-200" data-mp="1rend8dyduoyb">
            <ShowcaseWord word={t.showcase.words[1]} index={1} progress={progress} reduced={staticScene} data-id="element-201" data-mp="btbo73wjx5ut" />
          </div>

          {/* Layer 4b — editorial words as a single row (mobile) */}
          <div className="pointer-events-none absolute inset-x-0 bottom-[14%] flex items-center justify-center gap-4 px-6 md:hidden" data-id="element-202" data-mp="kthykgwydppr">
            {t.showcase.words.map((word, i) => <React.Fragment key={word} data-id="element-203" data-mp="3objnk2ujxh35" data-mpi={i}>
                {i > 0 && <span className="text-[0.6rem] text-magenta" data-id="element-204" data-mp="3t9tl2k3r0trx">·</span>}
                <ShowcaseWord word={word} index={i} progress={progress} reduced={staticScene} travel={16} className="tracking-[0.28em]" data-id="element-205" data-mp="3t8igii2hbhzr" />
              </React.Fragment>)}
          </div>
        </div>

        {/* Layer 5 — foreground grid, moving fastest */}
        <motion.div aria-hidden="true" style={staticScene ? {
        opacity: 0.25
      } : {
        y: gridY,
        willChange: 'transform'
      }} className="grid-lines pointer-events-none absolute -inset-y-40 inset-x-0 opacity-40" data-id="element-206" data-mp="scwlskj4u723" />

        {/* Seamless hand-off into the Features section */}
        <motion.div aria-hidden="true" style={staticScene ? {
        opacity: 0
      } : {
        opacity: exitOpacity
      }} className="pointer-events-none absolute inset-0 z-30 bg-gradient-to-b from-transparent via-[#07051A]/70 to-[#07051A]" data-id="element-207" data-mp="16c5zndjv8p2w" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-30 h-32 bg-gradient-to-b from-transparent to-[#07051A]" data-id="element-208" data-mp="in3dpti78a2l" />
      </div>
    </section>;
}